/* Lumistelle Customer Menu - client module (.lcm).
   SSR owns the markup (sections/lumistelle-customer-menu.liquid). This file owns
   only: open/close (one close authority), tab switching, the back gesture,
   mutual exclusion with the category menu and the wishlist modal, the Wishlist
   tab mount, and the For You carousels (engine carousels in lite mode, loaded
   by the shared block loader below).

   History: one entry, state {lcm:true}. Hand-offs reuse the outgoing sheet's
   entry instead of spending it, because a closing sheet's history.back() is
   async and would pop the incoming sheet's freshly pushed entry.

   Public API: window.LumistelleCustomerMenu = { open, close, toggle, isOpen, selectTab }
   Declarative trigger: any element with [data-lcm-toggle] (optional
   data-lcm-tab="wishlist" to open on a given tab). */
(function () {
  'use strict';

  var root = document.querySelector('[data-lcm-root]');
  if (!root) return;                                   // signed out: section renders nothing

  var WL = window.LumistelleWishlist || {};
  var HAMBURGER_SEL = '.lumi-hdr__menu-btn';
  var CLOSE_FALLBACK_MS = 450;
  var HANDOFF_WAIT_MS = 400;

  var tabs = root.querySelectorAll('[data-lcm-tab]');
  var tabrow = root.querySelector('.lcm__tabrow');

  var state = {
    open: false,
    closing: false,
    pending: false,                                    // waiting on another sheet's history pop
    historyPushed: false,
    suppressPop: false,
    tab: root.getAttribute('data-start-tab') || 'for-you'
  };
  var lastTrigger = null;

  function isOpen() { return state.open && !state.closing; }

  // ---- Tabs ----
  function panelFor(id) { return root.querySelector('[data-lcm-panel="' + id + '"]'); }

  function selectTab(id, opts) {
    opts = opts || {};
    if (!panelFor(id)) return;
    state.tab = id;
    for (var i = 0; i < tabs.length; i++) {
      var t = tabs[i];
      var on = t.getAttribute('data-lcm-tab') === id;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      if (on) t.removeAttribute('tabindex'); else t.setAttribute('tabindex', '-1');
      var p = panelFor(t.getAttribute('data-lcm-tab'));
      if (p) p.hidden = !on;                           // unhide BEFORE mounting: the engine measures on init
      if (on) {
        if (opts.focus) t.focus();
        centerTab(t);
      }
    }
    if (state.open) onTabShown(id);
  }

  function centerTab(t) {
    if (!tabrow || tabrow.scrollWidth <= tabrow.clientWidth) return;
    tabrow.scrollTo({ left: t.offsetLeft - tabrow.clientWidth / 2 + t.offsetWidth / 2, behavior: 'smooth' });
  }

  function onTabShown(id) {
    if (id === 'wishlist') mountWishlist();
    else if (id === 'for-you') showForYou();
  }

  function wireTabs() {
    root.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('[data-lcm-tab]');
      if (!t || !root.contains(t)) return;
      e.preventDefault();
      selectTab(t.getAttribute('data-lcm-tab'));
    });
    // Roving tabindex: arrows / Home / End move between tabs.
    if (tabrow) tabrow.addEventListener('keydown', function (e) {
      var keys = { ArrowRight: 1, ArrowLeft: -1, Home: 'first', End: 'last' };
      if (!(e.key in keys)) return;
      e.preventDefault();
      var ids = [];
      for (var i = 0; i < tabs.length; i++) ids.push(tabs[i].getAttribute('data-lcm-tab'));
      var cur = ids.indexOf(state.tab), k = keys[e.key], next;
      if (k === 'first') next = 0;
      else if (k === 'last') next = ids.length - 1;
      else next = (cur + k + ids.length) % ids.length;
      selectTab(ids[next], { focus: true });
    });
  }

  // ---- Wishlist tab: borrow the modal's content node ----
  function mountWishlist() {
    var mount = root.querySelector('[data-lcm-wishlist-mount]');
    if (!mount || !WL.mountContent) return;
    WL.mountContent(mount);
    if (WL.prepareGrid) WL.prepareGrid();              // idempotent: engine init + cards 11+ once
  }

  // ---- For You: carousel blocks ----
  // Every block is an empty engine scaffold (snippets/lumistelle-customer-menu-carousel).
  // A block loads when it scrolls near inside the For You panel, with the menu open:
  // fetch its cards, inject them, reveal it, decide autoplay, THEN init the engine.
  // A carousel can't take cards after init (its stub products array is built then).
  //
  // Autoplay: the first block (top down) that ends up with cards autoplays when
  // lead autoplay is on; any other block only if it has its own always-autoplay.
  // Upper blocks are therefore started before lower ones and awaited, which
  // costs nothing: they are nearer the top, so they are loading already.
  var LEAD_AUTOPLAY = root.getAttribute('data-lead-autoplay') === 'true';
  var forYouPanel = root.querySelector('[data-lcm-panel="for-you"]');
  var blocks = [];
  var pendingInits = [];
  var io = null;

  // ---- Sources: each returns Promise<Element[]> (cards in display order) ----
  var RV_KEY = 'lumistelle_recently_viewed_v1';

  function rvHandles(max, includeCurrent) {
    var current = includeCurrent ? '' : (document.body.getAttribute('data-product-handle') || '');
    try {
      var a = JSON.parse(localStorage.getItem(RV_KEY) || '[]');
      if (!Array.isArray(a)) return [];
      var out = [];
      for (var i = 0; i < a.length && out.length < max; i++) {
        var h = a[i];
        if (typeof h !== 'string' || !/^[a-z0-9][a-z0-9-]*$/i.test(h)) continue;
        if (h === current || out.indexOf(h) !== -1) continue;   // never the product being viewed
        out.push(h);
      }
      return out;
    } catch (e) { return []; }
  }

  // One request per handle per page load (shared by every block; nothing is
  // kept past the page). Each caller parses its own copy: a node can only
  // live in one carousel.
  var cardHtml = {};
  function cardHtmlFor(handle) {
    if (!cardHtml[handle]) {
      cardHtml[handle] = fetch('/products/' + encodeURIComponent(handle) + '?view=wishlist-card', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .catch(function () { return ''; });
    }
    return cardHtml[handle];
  }

  function fetchCard(handle) {
    return cardHtmlFor(handle).then(function (html) {
      if (!html) return null;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector('.lumistelle-carousel__product');
    });
  }

  function cardsFromHandles(handles) {
    return Promise.all(handles.map(fetchCard)).then(function (cards) {
      return cards.filter(Boolean);                    // 404s dropped; the PDP addon prunes storage
    });
  }

  // Your bag: the live cart, read when the block loads (not with the page,
  // since the cart changes during a visit). One card per product, bag order.
  var bagP = null;                                     // cart read shared by the carousels
  function bagHandles(max) {
    if (!bagP) bagP = readCart();
    return bagP.then(function (cart) { return handlesOf(cart).slice(0, max); });
  }

  function readCart() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function handlesOf(cart) {
    var out = [];
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var h = items[i].handle;
      if (h && out.indexOf(h) === -1) out.push(h);
    }
    return out;
  }

  // Which products the bag carousel shows, order-free: quantity changes and
  // reordering don't trigger a rebuild, only a different set of products.
  function bagKey(handles) {
    return handles.slice().sort().join(',');
  }

  // Rebuild the bag carousel in place when the set of products changed.
  // A carousel can't take cards after init, so: destroy the engine instance
  // (destroyComplete is per instance: observers, timers, listeners, registry),
  // swap in a pristine copy of the empty scaffold, fetch, inject, init.
  function rebuildBag(b, handles) {
    var token = (b.rebuildToken = (b.rebuildToken || 0) + 1);
    b.renderedKey = bagKey(handles);

    var engine = window.Lumistelle && window.Lumistelle.engine;
    var id = b.carousel.id;
    try {
      if (engine && typeof engine.destroyComplete === 'function') engine.destroyComplete(id);
    } catch (e) {}
    var i = pendingInits.indexOf(b);
    if (i !== -1) pendingInits.splice(i, 1);

    var fresh = b.carTemplate.cloneNode(true);
    b.car.parentNode.replaceChild(fresh, b.car);
    b.car = fresh;
    b.track = fresh.querySelector('[data-lcm-track]');
    b.carousel = fresh.querySelector('.lumistelle-carousel');

    cardsFromHandles(handles).then(function (cards) {
      if (token !== b.rebuildToken) return;            // a newer rebuild took over
      cards = cards.slice(0, b.max);
      b.shownHandles = cards.map(function (c) { return c.getAttribute('data-handle') || ''; });
      if (!cards.length) return;                        // stays hidden; buttons still follow the count
      var frag = document.createDocumentFragment();
      for (var k = 0; k < cards.length; k++) frag.appendChild(cards[k]);
      b.track.appendChild(frag);
      if (WL.fill) WL.fill(b.track);
      setAutoplay(b.carousel, b.autoplayOn);
      b.car.hidden = false;
      finishInit(b);
    });
  }

  // ---- Your bag: View bag + Checkout ----
  // Count and total are re-read on every open and after the cart drawer closes,
  // so they're always current (the bag cards themselves refresh per page).
  function wholeDollars(cents) {
    return '$' + Math.round((Number(cents) || 0) / 100).toLocaleString('en-US');
  }

  function refreshBagActions() {
    var p = readCart();
    if (!bagP) bagP = p;                               // first open: one cart read for everything
    p.then(function (cart) {
      if (!cart) return;
      var bag = null;
      for (var i = 0; i < blocks.length; i++) if (blocks[i].source === 'bag') bag = blocks[i];
      var actions = root.querySelector('[data-lcm-bag-actions]');
      var count = cart.item_count || 0;
      var has = count > 0;
      if (actions) {
        actions.hidden = !has;
        var btns = actions.querySelectorAll('[data-lcm-bag-btn]');
        for (var j = 0; j < btns.length; j++) {
          var info = btns[j].getAttribute('data-lcm-info');
          var out = btns[j].querySelector('[data-lcm-btn-info]');
          if (!out) continue;
          var text = info === 'count' ? String(count)
            : info === 'total' ? wholeDollars(cart.total_price) : '';
          out.textContent = text ? ' \u00b7 ' + text : '';
          out.hidden = !text || !has;
        }
      }
      if (bag) {
        if (bag.heading) bag.heading.hidden = !has;
        if (!has) {
          bag.car.hidden = true;
          return;
        }
        var handles = handlesOf(cart).slice(0, bag.max);
        if (bag.loaded && bagKey(handles) !== bag.renderedKey) {
          rebuildBag(bag, handles);                     // different products: rebuild
        } else if (bag.track.children.length) {
          bag.car.hidden = false;
        }
      }
    });
  }

  // Watch the cart drawer: when it closes over the open menu, refresh the numbers.
  function watchCartDrawer() {
    var drawer = document.getElementById('cart-drawer-container');
    if (!drawer || !('MutationObserver' in window)) return;
    var wasOpen = drawer.classList.contains('is-open');
    new MutationObserver(function () {
      var nowOpen = drawer.classList.contains('is-open');
      if (wasOpen && !nowOpen && state.open) refreshBagActions();
      wasOpen = nowOpen;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  // Recommended for you.
  // Seeds: the two most recently viewed products (including the one on screen),
  // else the newest wishlist item. No seeds, or nothing left after exclusions:
  // the fallback collection under the fallback heading.
  var RECS_URL = root.getAttribute('data-recs-url') || '/recommendations/products';
  var RECS_INTENT = root.getAttribute('data-recs-intent') || 'related';
  var COLLECTIONS_URL = root.getAttribute('data-collections-url') || '/collections';
  var RECS_FALLBACK = root.getAttribute('data-recs-fallback') || 'best-selling-products';
  var RECS_FALLBACK_HEADING = root.getAttribute('data-recs-fallback-heading') || '';
  var MENU_CARDS = 'lumistelle-menu-cards';

  // [data-mc-item] wrappers from lumistelle-menu-cards -> card elements,
  // each carrying data-published from its wrapper.
  function cardsFromSection(html) {
    if (!html) return [];
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var items = doc.querySelectorAll('[data-mc-item]');
    var out = [];
    for (var i = 0; i < items.length; i++) {
      var card = items[i].querySelector('.lumistelle-carousel__product');
      if (!card) continue;
      card.setAttribute('data-published', items[i].getAttribute('data-published') || '');
      out.push(card);
    }
    return out;
  }

  function fetchSection(url) {
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(cardsFromSection)
      .catch(function () { return []; });
  }

  function recsFor(productId) {
    return fetchSection(RECS_URL + '?section_id=' + MENU_CARDS +
      '&product_id=' + encodeURIComponent(productId) +
      '&limit=10&intent=' + encodeURIComponent(RECS_INTENT));
  }

  function collectionCards(handle, query) {
    return fetchSection(COLLECTIONS_URL.replace(/\/$/, '') + '/' + encodeURIComponent(handle) +
      '?section_id=' + MENU_CARDS + (query || ''));
  }

  function idFromHandle(handle) {
    return cardHtmlFor(handle).then(function (html) {
      var m = html && html.match(/data-product-id="(\d+)"/);
      return m ? m[1] : null;
    });
  }

  function wishlistIds() {
    var ids = (window.LumistelleWishlist && window.LumistelleWishlist.ids) || [];
    return ids.map(String);
  }

  function recsSeeds() {
    return Promise.all(rvHandles(2, true).map(idFromHandle)).then(function (ids) {
      ids = ids.filter(Boolean);
      if (!ids.length) {
        var wl = wishlistIds();
        if (wl.length) ids = [wl[0]];
      }
      return ids;
    });
  }

  // Keep a card unless its product is already somewhere she has it.
  function makeFilter(bag) {
    var handles = {}, ids = {};
    bag.concat(rvHandles(999, true)).forEach(function (h) { handles[h] = 1; });
    wishlistIds().forEach(function (id) { ids[id] = 1; });
    return function (cards, max) {
      var out = [], seen = {};
      for (var i = 0; i < cards.length && out.length < max; i++) {
        var h = cards[i].getAttribute('data-handle') || '';
        var id = cards[i].getAttribute('data-product-id') || '';
        if (!h || seen[h] || handles[h] || ids[id]) continue;
        seen[h] = 1;
        out.push(cards[i]);
      }
      return out;
    };
  }

  function recsSource(b) {
    return Promise.all([recsSeeds(), bagHandles(999)]).then(function (r) {
      var seeds = r[0], keep = makeFilter(r[1]);
      var recsP = seeds.length
        ? Promise.all(seeds.map(recsFor)).then(function (lists) { return [].concat.apply([], lists); })
        : Promise.resolve([]);
      return recsP.then(function (cards) {
        var out = keep(cards, b.max);
        if (out.length) return out;
        if (RECS_FALLBACK_HEADING) b.headingText = RECS_FALLBACK_HEADING;
        return collectionCards(RECS_FALLBACK).then(function (fb) { return keep(fb, b.max); });
      });
    });
  }

  // New since last visit.
  // Baseline = last activity from the PREVIOUS browser session, fixed for the
  // whole session (sessionStorage), so browsing pages doesn't move it. Last
  // activity is refreshed on every page. Timestamps only, no cards stored.
  var LAST_SEEN_KEY = 'lumistelle_last_seen_v1';
  var BASELINE_KEY = 'lumistelle_visit_baseline_v1';
  var NEW_COLLECTION = root.getAttribute('data-new-collection') || 'all';
  var NEW_FALLBACK_HEADING = root.getAttribute('data-new-fallback-heading') || '';

  var visitBaseline = (function () {
    var now = Math.floor(Date.now() / 1000), base = 0;
    try {
      var s = sessionStorage.getItem(BASELINE_KEY);
      if (s === null) {
        s = localStorage.getItem(LAST_SEEN_KEY) || '0';   // previous session's last activity
        sessionStorage.setItem(BASELINE_KEY, s);
      }
      base = parseInt(s, 10) || 0;
      localStorage.setItem(LAST_SEEN_KEY, String(now));
    } catch (e) {}
    return base;                                          // 0 = first visit (or storage unavailable)
  })();

  function newSource(b) {
    return Promise.all([
      collectionCards(NEW_COLLECTION, '&sort_by=created-descending'),
      bagHandles(999)
    ]).then(function (r) {
      var cards = r[0], keep = makeFilter(r[1]);
      if (visitBaseline) {
        cards = cards.filter(function (c) {
          return (parseInt(c.getAttribute('data-published'), 10) || 0) > visitBaseline;
        });
      } else if (NEW_FALLBACK_HEADING) {
        b.headingText = NEW_FALLBACK_HEADING;             // first visit: just the newest pieces
      }
      return keep(cards, 24);                              // extra headroom for cross-block dedupe
    });
  }

  var SOURCES = {
    bag: function (b) {
      return bagHandles(b.max).then(function (h) {
        b.renderedKey = bagKey(h);
        return cardsFromHandles(h);
      });
    },
    recent: function (b) { return cardsFromHandles(rvHandles(b.max)); },
    recs: recsSource,
    new: newSource
  };

  function fetchFor(b) {
    var fn = SOURCES[b.source];
    if (!fn) return Promise.resolve([]);
    return Promise.resolve().then(function () { return fn(b); })
      .catch(function () { return []; });
  }

  // ---- Block lifecycle ----
  function initCarousel(el) {
    if (el.hasAttribute('data-initialized')) return;
    if (window.Lumistelle && typeof window.Lumistelle.initLumistelleSection === 'function') {
      window.Lumistelle.initLumistelleSection(el);
    } else {
      document.addEventListener('lumistelle:engine:ready', function () {
        if (!el.hasAttribute('data-initialized')) window.Lumistelle.initLumistelleSection(el);
      }, { once: true });
    }
  }

  // The engine measures on init, so only init while the panel is actually shown.
  function canMeasure() {
    return state.open && forYouPanel && !forYouPanel.hidden;
  }

  function finishInit(b) {
    if (!canMeasure()) { if (pendingInits.indexOf(b) === -1) pendingInits.push(b); return; }
    initCarousel(b.carousel);
  }

  function flushInits() {
    if (!canMeasure()) return;
    var list = pendingInits.splice(0);
    for (var i = 0; i < list.length; i++) initCarousel(list[i].carousel);
  }

  function setAutoplay(carousel, on) {
    try {
      var cfg = JSON.parse(carousel.getAttribute('data-config') || '{}');
      cfg.autoplay = !!on;
      carousel.setAttribute('data-config', JSON.stringify(cfg));
    } catch (e) {}
  }

  function startBlock(b) {
    if (b.started) return;
    b.started = true;
    for (var j = 0; j < b.index; j++) startBlock(blocks[j]);   // upper blocks decide the lead

    var cardsP = b.prefetch || fetchFor(b);
    var above = blocks.slice(0, b.index).map(function (x) { return x.settled; });

    Promise.all([cardsP, Promise.all(above)]).then(function (res) {
      var cards = res[0] || [], aboveRendered = res[1];
      if (b.dedupe) {
        var shown = {};
        for (var k = 0; k < b.index; k++) {
          (blocks[k].shownHandles || []).forEach(function (h) { shown[h] = 1; });
        }
        cards = cards.filter(function (c) { return !shown[c.getAttribute('data-handle') || '']; });
      }
      cards = cards.slice(0, b.max);
      b.shownHandles = cards.map(function (c) { return c.getAttribute('data-handle') || ''; });
      b.loaded = true;
      // An empty bag at load never led; if it fills later, it only autoplays
      // with its own toggle, so two carousels never autoplay at once.
      b.autoplayOn = b.alwaysAutoplay;
      if (!cards.length) {
        if (b.empty) b.empty.hidden = false;       // (bag heading/buttons follow the cart count instead)
        b.resolve(false);
        return;
      }
      var frag = document.createDocumentFragment();
      for (var i = 0; i < cards.length; i++) frag.appendChild(cards[i]);
      b.track.appendChild(frag);
      if (WL.fill) WL.fill(b.track);                   // hearts reflect saved state

      var isLead = aboveRendered.indexOf(true) === -1;
      b.autoplayOn = (isLead && LEAD_AUTOPLAY) || b.alwaysAutoplay;
      setAutoplay(b.carousel, b.autoplayOn);

      if (b.heading) {
        if (b.headingText) b.heading.textContent = b.headingText;
        b.heading.hidden = false;
      }
      b.car.hidden = false;                            // visible BEFORE init: the engine measures
      b.resolve(true);
      finishInit(b);
    });
  }

  function collectBlocks() {
    var els = root.querySelectorAll('[data-lcm-block]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var b = {
        index: i,
        el: el,
        key: el.getAttribute('data-lcm-block'),
        source: el.getAttribute('data-lcm-source'),
        max: parseInt(el.getAttribute('data-lcm-max'), 10) || 10,
        eager: el.hasAttribute('data-lcm-eager'),
        alwaysAutoplay: el.hasAttribute('data-lcm-always-autoplay'),
        dedupe: el.hasAttribute('data-lcm-dedupe'),
        shownHandles: [],
        heading: el.querySelector('[data-lcm-heading]'),
        car: el.querySelector('[data-lcm-car]'),
        track: el.querySelector('[data-lcm-track]'),
        carousel: el.querySelector('.lumistelle-carousel'),
        empty: el.querySelector('[data-lcm-empty]'),
        started: false,
        prefetch: null
      };
      if (!b.car || !b.track || !b.carousel) continue;
      // Pristine copy of the empty scaffold, taken before any init, for rebuilds.
      b.carTemplate = b.car.cloneNode(true);
      var tc = b.carTemplate.querySelector('.lumistelle-carousel');
      if (tc) tc.removeAttribute('data-initialized');
      b.settled = new Promise(function (r) { b.resolve = r; });
      b.index = blocks.length;
      blocks.push(b);
    }
  }

  // Load each block as it comes within one screen of the panel's viewport.
  function observeBlocks() {
    if (!blocks.length || !forYouPanel) return;
    if (!('IntersectionObserver' in window)) return;   // showForYou starts all instead
    io = new IntersectionObserver(function (entries) {
      if (!canMeasure()) return;
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        var el = entries[i].target;
        for (var k = 0; k < blocks.length; k++) {
          if (blocks[k].el === el) { io.unobserve(el); startBlock(blocks[k]); }
        }
      }
    }, { root: forYouPanel, rootMargin: '0px 0px 100% 0px' });
    for (var i = 0; i < blocks.length; i++) io.observe(blocks[i].el);
  }

  function showForYou() {
    if (!io) {                                          // no IntersectionObserver: load everything
      for (var i = 0; i < blocks.length; i++) startBlock(blocks[i]);
    }
    flushInits();
  }

  // Head start: begin first-screen fetches when the finger lands on the toggle,
  // before the tap completes. Fetch only; injection waits for the open menu.
  function prefetchEager(tab) {
    if ((tab || state.tab) !== 'for-you') return;
    for (var i = 0; i < blocks.length; i++) {
      var b = blocks[i];
      if (b.eager && !b.started && !b.prefetch) b.prefetch = fetchFor(b);
    }
  }

  // ---- Open / close ----
  function setExpanded(on) {
    var ts = document.querySelectorAll('[data-lcm-toggle]');
    for (var i = 0; i < ts.length; i++) ts[i].setAttribute('aria-expanded', on ? 'true' : 'false');
  }

  // Run fn after the next popstate (another sheet spending its entry), with a
  // fallback in case that pop never comes.
  function afterPop(fn) {
    var done = false;
    function go() {
      if (done) return;
      done = true;
      window.removeEventListener('popstate', go);
      fn();
    }
    window.addEventListener('popstate', go);
    setTimeout(go, HANDOFF_WAIT_MS);
  }

  function open(tabId) {
    if (state.open || state.closing || state.pending) return;
    if (tabId) state.tab = tabId;

    // Wishlist modal: close it silently (fromPop) and reuse its history entry.
    if (WL.isModalOpen && WL.isModalOpen() && WL.closeModal) WL.closeModal(true);

    // Category menu: it has no API and spends its history entry asynchronously,
    // so close it through its own button and open only after its pop lands.
    if (document.body.classList.contains('mobile-menu-open')) {
      var hb = document.querySelector(HAMBURGER_SEL);
      var theirEntry = !!(history.state && history.state.lumiMenu);
      if (hb) {
        if (theirEntry) {
          state.pending = true;
          afterPop(function () { state.pending = false; doOpen(); });
          hb.click();
          return;
        }
        hb.click();
      }
    }
    doOpen();
  }

  function doOpen() {
    if (state.open) return;
    state.open = true;
    root.classList.add('lcm--mounted');                // display first, so the fade has a start frame
    root.removeAttribute('aria-hidden');
    document.body.classList.add('lcm-open');
    setExpanded(true);
    selectTab(state.tab);                              // unhides the panel, then mounts its content
    flushInits();
    refreshBagActions();
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { root.classList.add('lcm--open'); });
    });
    try {
      if (history.state && history.state.lwm) history.replaceState({ lcm: true }, '');
      else history.pushState({ lcm: true }, '');
      state.historyPushed = true;
    } catch (e) {}
  }

  // Single close authority.
  // opts.fromPop  - the entry is already gone; don't spend it.
  // opts.handoff  - another sheet will reuse our entry; don't spend it, don't move focus.
  function close(opts) {
    opts = opts || {};
    if (!state.open || state.closing) return;
    state.closing = true;

    var active = document.activeElement;
    if (active && root.contains(active) && active.blur) active.blur();   // aria-hidden can't cover focus

    root.classList.remove('lcm--open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('lcm-open');
    setExpanded(false);

    var finished = false;
    function finish() {
      if (finished) return;
      finished = true;
      root.removeEventListener('transitionend', onEnd);
      root.classList.remove('lcm--mounted');
      state.open = false;
      state.closing = false;
      if (!opts.handoff && lastTrigger && lastTrigger.focus) {
        try { lastTrigger.focus({ preventScroll: true }); } catch (e) {}
      }
    }
    function onEnd(e) {
      if (e.target === root && e.propertyName === 'opacity') finish();
    }
    root.addEventListener('transitionend', onEnd);
    setTimeout(finish, CLOSE_FALLBACK_MS);             // reduced motion never fires transitionend

    if (state.historyPushed && !opts.fromPop && !opts.handoff) {
      state.historyPushed = false;
      state.suppressPop = true;
      try { history.back(); } catch (e) { state.suppressPop = false; }
    } else {
      state.historyPushed = false;
    }
  }

  function toggle(tabId) {
    if (state.open && !state.closing) {
      if (tabId && tabId !== state.tab) selectTab(tabId);   // heart while on another tab: switch, don't close
      else close();
    } else open(tabId);
  }

  // ---- Global wiring ----
  function wireGlobal() {
    // Declarative trigger (header account button, added in the header pass).
    // Capture phase so nothing downstream treats it as a plain link.
    document.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('[data-lcm-toggle]');
      if (!t) return;
      e.preventDefault();
      e.stopPropagation();
      lastTrigger = t;
      toggle(t.getAttribute('data-lcm-tab') || null);
    }, true);

    // View bag: open the cart drawer ON TOP of the menu (z 1050 vs 90). Closing
    // the drawer returns to the menu; its history entry sits above ours, and our
    // popstate ignores pops while our marker is still on the stack. Capture
    // phase stops the drawer's own a[href$="/cart"] listener opening it twice.
    // No CartDrawer: the link navigates to the cart page.
    document.addEventListener('click', function (e) {
      var b = e.target.closest && e.target.closest('[data-lcm-bag-btn="viewbag"]');
      if (!b || !window.CartDrawer || typeof window.CartDrawer.open !== 'function') return;
      e.preventDefault();
      e.stopPropagation();
      window.CartDrawer.open();
    }, true);

    // Head start for first-screen carousels.
    document.addEventListener('pointerdown', function (e) {
      if (state.open) return;
      var t = e.target.closest && e.target.closest('[data-lcm-toggle]');
      if (t) prefetchEager(t.getAttribute('data-lcm-tab'));
    }, { capture: true, passive: true });

    // Hamburger while we're open: close ours first, then let the category menu
    // open once our entry is spent. Runs before its own capture listener on the
    // button because document capture precedes target capture.
    document.addEventListener('click', function (e) {
      if (!state.open || state.closing) return;        // the re-dispatched click passes through
      var hb = e.target.closest && e.target.closest(HAMBURGER_SEL);
      if (!hb) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      var spent = state.historyPushed;
      close();
      if (spent) afterPop(function () { hb.click(); });
      else hb.click();
    }, true);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) close();
    });

    window.addEventListener('popstate', function () {
      if (state.suppressPop) { state.suppressPop = false; return; }   // our own history.back()
      if (history.state && history.state.lcm) return;  // a sheet above us (quick view, gallery) closed
      if (isOpen()) close({ fromPop: true });
    });
  }

  root.setAttribute('aria-hidden', 'true');
  collectBlocks();
  observeBlocks();
  watchCartDrawer();
  wireTabs();
  wireGlobal();

  window.LumistelleCustomerMenu = {
    open: open,
    close: close,
    toggle: toggle,
    isOpen: isOpen,
    selectTab: selectTab
  };
})();
